import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';

import { db, storage, getDocs, collection } from '../LoginComp/firebase';
import {
  doc, setDoc, updateDoc, deleteDoc, addDoc, serverTimestamp,
  getDoc, query, where, orderBy,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import './consultant.css';

function Consultant() {
  const [selectedOption, setSelectedOption] = useState('viewRecoveryProfile');
  const [openMenu, setOpenMenu] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const auth = getAuth();

  // ============ LOGGED-IN CONSULTANT ============
  const [currentUser, setCurrentUser] = useState(null);

  // ============ RECOVERY PROFILES STATE ============
  const [vrpProfiles, setVrpProfiles] = useState([]);
  const [vrpLoading, setVrpLoading] = useState(false);
  const [vrpError, setVrpError] = useState('');
  const [vrpSearch, setVrpSearch] = useState('');

  // View modal
  const [viewModal, setViewModal] = useState({ open: false, profile: null });

  // ============ FOLLOW-UP REMINDER STATE ============
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null); // { patient, stage }
  const [followUpLanguage, setFollowUpLanguage] = useState('english');
  const [englishFollowUpMsg, setEnglishFollowUpMsg] = useState('');
  const [sinhalaFollowUpMsg, setSinhalaFollowUpMsg] = useState('');

  // ============ UTILITIES ============
  const toDate = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value === 'object' && 'seconds' in value) return new Date(value.seconds * 1000);
    if (typeof value === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      const dt = new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
      return isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const getDaysUntil = (dateStr) => {
    const target = toDate(dateStr);
    if (!target) return 999;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
  };

  const formatFollowUpDate = (dateStr) => {
    const d = toDate(dateStr);
    if (!d) return '—';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  };

  // Read the follow-up date from a stage — handles BOTH spellings
  const getStageNextDate = (stage) => {
    if (!stage) return null;
    return stage.nextFollowUpDate || stage.nextfollowUpDate || null;
  };

  const prettyCondition = (c) =>
    c ? c.replace(/-/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase()) : '—';

  // ============ CLOSE DROPDOWN ON OUTSIDE CLICK ============
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    setSelectedOption(option);
    setOpenMenu(null);
  };

  const toggleDropdown = (menu) => setOpenMenu(openMenu === menu ? null : menu);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setOpenMenu(null);
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      sessionStorage.clear();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Error logging out. Please try again.');
      setIsLoggingOut(false);
    }
  };

  // ============ RESOLVE LOGGED-IN CONSULTANT ============
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return setCurrentUser(null);
      try {
        const q = query(collection(db, 'users'), where('email', '==', user.email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setCurrentUser({ uid: user.uid, docId: d.id, ...d.data() });
        } else {
          setCurrentUser({ uid: user.uid, email: user.email });
        }
      } catch (e) {
        console.error('resolve user error:', e);
      }
    });
    return () => unsub();
  }, [auth]);

  // ============ FETCH ONLY THIS CONSULTANT'S PROFILES ============
  const fetchRecoveryProfiles = async () => {
    if (!currentUser) return;
    setVrpLoading(true);
    setVrpError('');

    try {
      const col = collection(db, 'recovery-profile'); // ✅ correct collection name
      let merged = [];

      // ---- Query 1: by assignedConsultantId ----
      const consultantId = currentUser.docId || currentUser.uid;
      if (consultantId) {
        const q1 = query(col, where('assignedConsultantId', '==', consultantId));
        const s1 = await getDocs(q1);
        merged = s1.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      // ---- Query 2: by assignedConsultantEmail (fallback) ----
      if (currentUser.email) {
        const q2 = query(
          col,
          where('assignedConsultantEmail', '==', currentUser.email)
        );
        const s2 = await getDocs(q2);
        const byEmail = s2.docs.map((d) => ({ id: d.id, ...d.data() }));

        const seen = new Set(merged.map((p) => p.id));
        byEmail.forEach((p) => {
          if (!seen.has(p.id)) merged.push(p);
        });
      }

      console.log('📦 profiles for consultant:', merged.length);
      setVrpProfiles(merged);
    } catch (e) {
      console.error('❌ fetch profiles error:', e);
      setVrpError('Failed to load recovery profiles: ' + e.message);
    } finally {
      setVrpLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) fetchRecoveryProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // ============ FILTER ============
  const vrpFiltered = vrpProfiles.filter((p) => {
    const q = vrpSearch.toLowerCase();
    const stageMatch = (p.stages || []).some((s) =>
      (s.stageName || '').toLowerCase().includes(q)
    );
    return (
      (p.patientName || '').toLowerCase().includes(q) ||
      (p.patientPhone || '').toLowerCase().includes(q) ||
      (p.condition || '').toLowerCase().includes(q) ||
      (p.assignedConsultant || '').toLowerCase().includes(q) ||
      stageMatch
    );
  });

  const openViewModal = (profile) => setViewModal({ open: true, profile });

  // ============ OPEN FOLLOW-UP REMINDER MODAL ============
  const openFollowUpModal = (patient, stage, language = 'english') => {
    setSelectedFollowUp({ patient, stage });
    setFollowUpLanguage(language);

    const nextDate = getStageNextDate(stage);
    const dateStr = formatFollowUpDate(nextDate);
    const consultant = patient.assignedConsultant || 'your consultant';

    setEnglishFollowUpMsg(
`Dear ${patient.patientName},

This is a friendly reminder from Southern Rehab Care.

Your next follow-up for stage "${stage.stageName || 'N/A'}" is scheduled on ${dateStr}.

👨‍⚕️ Consultant: ${consultant}
🩺 Specialization: ${patient.assignedConsultantSpecialization || '-'}

Please contact us if you need to reschedule.

Thank you,
Southern Rehab Care
Galle`
    );

    setSinhalaFollowUpMsg(
`ගරු ${patient.patientName},

Southern Rehab Care වෙතින් මතක් කිරීමක්.

ඔබගේ "${stage.stageName || 'N/A'}" අදියර සඳහා ඊළඟ නැවත පරීක්ෂණය ${dateStr} දිනට නියමිතය.

👨‍⚕️ උපදේශක: ${consultant}
🩺 විශේෂඥතාව: ${patient.assignedConsultantSpecialization || '-'}

දිනය වෙනස් කිරීමට අවශ්‍ය නම් අප හා සම්බන්ධ වන්න.

ස්තූතියි,
Southern Rehab Care
Galle`
    );

    setShowFollowUpModal(true);
  };

  // ============ SEND VIA WHATSAPP ============
  const sendFollowUpWhatsApp = (patient, language) => {
    const phone = (patient.patientPhone || '').replace(/\D/g, '');
    if (!phone) { alert('❌ No phone number available for this patient'); return; }
    const intlPhone = phone.startsWith('0') ? '94' + phone.substring(1) : phone;
    const message = language === 'english' ? englishFollowUpMsg : sinhalaFollowUpMsg;
    window.open(`https://wa.me/${intlPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // ============ SEND VIA SMS ============
  const sendFollowUpSMS = (patient, language) => {
    const phone = (patient.patientPhone || '').replace(/\D/g, '');
    if (!phone) { alert('❌ No phone number available for this patient'); return; }
    const message = language === 'english' ? englishFollowUpMsg : sinhalaFollowUpMsg;
    window.location.href = `sms:${phone}?body=${encodeURIComponent(message)}`;
  };

  // ============ NATIVE CALL ============
  const callPatient = (patient) => {
    const phone = (patient.patientPhone || '').replace(/\D/g, '');
    if (!phone) { alert('❌ No phone number available'); return; }
    window.location.href = `tel:${phone}`;
  };

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <>
      {/* TOP NAVIGATION BAR */}
      {/* <nav className="top-nav" ref={dropdownRef}>
        <div className="logo">Powered By Glaxmore IT</div>

        <ul className="nav-links">
          <li
            className={`nav-single-btn ${selectedOption === 'viewRecoveryProfile' ? 'active-nav' : ''}`}
            onClick={() => handleSelect('viewRecoveryProfile')}
          >
            <span className="dropdown-label"> View Recovery Profiles</span>
          </li>

          <li
            className={`logout-btn ${isLoggingOut ? 'logging-out' : ''}`}
            onClick={(e) => { e.stopPropagation(); handleLogout(); }}
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </li>
        </ul>
      </nav> */}





<nav className="top-nav" ref={dropdownRef}>
  <div className="logo">Powered By Glaxmore IT</div>

  <ul className="nav-links">
    {/* 👋 Greeting */}
    {currentUser && (
      <li className="nav-greeting">
        👋 Hello,{' '}
        <strong>
          {currentUser.firstName || ''} {currentUser.lastName || ''}
        </strong>

      </li>
    )}

    <li
      className={`nav-single-btn ${selectedOption === 'viewRecoveryProfile' ? 'active-nav' : ''}`}
      onClick={() => handleSelect('viewRecoveryProfile')}
    >
      <span className="dropdown-label"> View Recovery Profiles</span>
    </li>

    <li
      className={`logout-btn ${isLoggingOut ? 'logging-out' : ''}`}
      onClick={(e) => { e.stopPropagation(); handleLogout(); }}
    >
      {isLoggingOut ? 'Logging out...' : 'Logout'}
    </li>
  </ul>
</nav>





      {/* PAGE BODY */}
      <div className="page-body">

        {/************************************* VIEW RECOVERY PROFILES *********************************************/}
        {selectedOption === 'viewRecoveryProfile' && (
          <div className="View-Recovery-Profile-wrapper">
            <h1 className="View-Recovery-Profile-title">📋 View Recovery Profile</h1>

            <div className="vrp-toolbar">
              <input
                type="text"
                className="vrp-search-input"
                placeholder="🔍 Search by patient name, phone, or stage..."
                value={vrpSearch}
                onChange={(e) => setVrpSearch(e.target.value)}
              />
              <button className="vrp-refresh-btn" onClick={fetchRecoveryProfiles}>
                🔄 Refresh
              </button>
            </div>

            <div className="vrp-stats">
              My Assigned Profiles: <strong>{vrpFiltered.length}</strong>
            </div>

            {vrpLoading && <p className="vrp-loading">Loading recovery profiles...</p>}
            {vrpError && <p className="vrp-error">⚠️ {vrpError}</p>}

            {!vrpLoading && !vrpError && (
              <div className="vrp-table-wrapper">
                <table className="vrp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Patient</th>
                      <th>Phone</th>
                      <th>Condition</th>
                      <th>Follow-Up(s)</th>
                      <th>Start Date</th>
                      <th>Next Follow-Up</th>
                      <th>Consultant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vrpFiltered.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="vrp-empty">
                          <span style={{ fontSize: '38px' }}>📭</span>
                          <p>No recovery profiles assigned to you</p>
                        </td>
                      </tr>
                    ) : (
                      vrpFiltered.map((profile, index) => (
                        <tr
                          key={profile.id}
                          onClick={() => openViewModal(profile)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{index + 1}</td>
                          <td><strong>{profile.patientName || '—'}</strong></td>
                          <td>{profile.patientPhone || '—'}</td>
                          <td>{prettyCondition(profile.condition)}</td>
                          <td>
                            {(profile.stages || []).length === 0 ? (
                              <span>—</span>
                            ) : (
                              (profile.stages || []).map((s, i) => (
                                <span
                                  key={i}
                                  className="vrp-stage-badge"
                                  style={{ marginRight: 4 }}
                                >
                                  {s.stageName || 'N/A'}
                                </span>
                              ))
                            )}
                          </td>
                          <td>{profile.recoveryStartDate || '—'}</td>
                          <td>
                            {(profile.stages || []).length === 0
                              ? (profile.nextFollowUp || '—')
                              : ((profile.stages || [])
                                  .map((s) => getStageNextDate(s))
                                  .filter(Boolean)
                                  .join(' • ') || profile.nextFollowUp || '—')}
                          </td>
                          <td>{profile.assignedConsultant || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ============================= VIEW MODAL ============================= */}
            {viewModal.open && viewModal.profile && (
              <div
                className="vrp-modal-overlay"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setViewModal({ open: false, profile: null });
                  }
                }}
              >
                <div className="vrp-modal-content vrp-modal-lg">
                  <div className="vrp-modal-header">
                    <h3>👁️ Recovery Profile — {viewModal.profile.patientName}</h3>
                    <button
                      className="vrp-modal-close"
                      onClick={() => setViewModal({ open: false, profile: null })}
                    >
                      ×
                    </button>
                  </div>

                  <div className="vrp-modal-body">
                    {/* ---------- PATIENT BASIC INFO ---------- */}
                    <div className="vrp-detail-grid">
                    
                      <div><strong>Patient Name:</strong> {viewModal.profile.patientName || '—'}</div>
                      <div><strong>Gender:</strong> {viewModal.profile.patientGender || '—'}</div>
                      <div><strong>DOB:</strong> {viewModal.profile.patientDob || '—'}</div>
                      <div><strong>Phone:</strong> {viewModal.profile.patientPhone || '—'}</div>
                      <div><strong>Email:</strong> {viewModal.profile.patientEmail || '—'}</div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Address:</strong> {viewModal.profile.patientAddress || '—'}
                      </div>
                    </div>

                    <hr />

                    {/* ---------- CLINICAL INFO ---------- */}
                    <div className="vrp-detail-grid">
                      <div><strong>Condition:</strong> {prettyCondition(viewModal.profile.condition)}</div>
                      <div><strong>Start Height:</strong> {viewModal.profile.height || '—'}</div>
                      <div><strong>Start Weight:</strong> {viewModal.profile.weight || '—'}</div>
                      <div><strong>Menarche:</strong> {viewModal.profile.menarche || '—'}</div>
                      <div><strong>Recovery Start:</strong> {viewModal.profile.recoveryStartDate || '—'}</div>
                      <div><strong>Next Follow-Up:</strong> {viewModal.profile.nextFollowUp || '—'}</div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Treatment Plan:</strong> {viewModal.profile.treatmentPlan || '—'}
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Remarks:</strong> {viewModal.profile.remarks || '—'}
                      </div>
                    </div>

                    <hr />

                    {/* ---------- ASSIGNED CONSULTANT ---------- */}
                    <div className="vrp-detail-grid">
                      <div><strong>Consultant:</strong> {viewModal.profile.assignedConsultant || '—'}</div>
                      <div><strong>Email:</strong> {viewModal.profile.assignedConsultantEmail || '—'}</div>
                      <div><strong>Specialization:</strong> {viewModal.profile.assignedConsultantSpecialization || '—'}</div>
                    </div>

                    <hr />

                    {/* ---------- STAGES TABLE ---------- */}
                    <h4 style={{ marginTop: 12 }}>🩺 Recovery Stages</h4>
                    {(viewModal.profile.stages || []).length === 0 ? (
                      <p className="vrp-empty-text">No stages recorded yet.</p>
                    ) : (
                      <div className="vrp-table-wrapper">
                        <table className="vrp-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Follow-Up(s)</th>
                              <th>Followed Date</th>
                              <th>Next Follow-Up</th>
                              <th>Height</th>
                              <th>Weight</th>
                              <th>Notes</th>
                              {/* <th>Actions</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {(viewModal.profile.stages || []).map((stage, i) => {
                              const nextDate = getStageNextDate(stage);
                              return (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td><strong>{stage.stageName || 'N/A'}</strong></td>
                                  <td>{stage.followUpDate || '—'}</td>
                                  <td>{formatFollowUpDate(nextDate)}</td>
                                  <td>{stage.followupheight || stage.height || '—'}</td>
                                  <td>{stage.followupweight || '—'}</td>
                                  <td>{stage.notes || '—'}</td>
                                  {/* <td>
                                    {nextDate && (
                                      <button
                                        className="vrp-btn vrp-btn-blue"
                                        onClick={() =>
                                          openFollowUpModal(viewModal.profile, stage, 'english')
                                        }
                                      >
                                        📩 Remind
                                      </button>
                                    )}
                                  </td> */}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="vrp-modal-footer">
                    <button
                      className="vrp-btn vrp-btn-gray"
                      onClick={() => setViewModal({ open: false, profile: null })}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ============================= FOLLOW-UP REMINDER MODAL ============================= */}
            {showFollowUpModal && selectedFollowUp && (
              <div
                className="modal-overlay"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowFollowUpModal(false);
                    setSelectedFollowUp(null);
                  }
                }}
              >
                <div className="modal-content birthday-modal">
                  <div className="modal-header">
                    <h3>
                      📋 Follow-up Reminder — {selectedFollowUp.patient.patientName}{' '}
                      ({selectedFollowUp.stage.stageName || 'N/A'})
                    </h3>
                    <button
                      className="modal-close"
                      onClick={() => {
                        setShowFollowUpModal(false);
                        setSelectedFollowUp(null);
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div className="lang-tabs">
                    <button
                      className={`lang-tab ${followUpLanguage === 'english' ? 'active' : ''}`}
                      onClick={() => setFollowUpLanguage('english')}
                    >
                      🇬🇧 English
                    </button>
                    <button
                      className={`lang-tab ${followUpLanguage === 'sinhala' ? 'active' : ''}`}
                      onClick={() => setFollowUpLanguage('sinhala')}
                    >
                      🇱🇰 සිංහල
                    </button>
                  </div>

                  <div className="message-preview">
                    <label>Message Preview:</label>
                    <textarea
                      className="birthday-textarea"
                      value={followUpLanguage === 'english' ? englishFollowUpMsg : sinhalaFollowUpMsg}
                      onChange={(e) => {
                        if (followUpLanguage === 'english') setEnglishFollowUpMsg(e.target.value);
                        else setSinhalaFollowUpMsg(e.target.value);
                      }}
                      rows="8"
                    />
                  </div>

                  <div className="send-options">
                    <h4>Send via:</h4>
                    <div className="send-buttons">
                      <button
                        className="btn-whatsapp"
                        onClick={() => sendFollowUpWhatsApp(selectedFollowUp.patient, followUpLanguage)}
                      >
                        💬 WhatsApp
                      </button>
                      <button
                        className="btn-sms"
                        onClick={() => sendFollowUpSMS(selectedFollowUp.patient, followUpLanguage)}
                      >
                        📱 SMS
                      </button>
                      <button
                        className="btn-call"
                        onClick={() => callPatient(selectedFollowUp.patient)}
                      >
                        📞 Call
                      </button>
                      <button
                        className="btn-copy"
                        onClick={() => {
                          const text = followUpLanguage === 'english' ? englishFollowUpMsg : sinhalaFollowUpMsg;
                          navigator.clipboard.writeText(text);
                          alert('✅ Message copied to clipboard!');
                        }}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setShowFollowUpModal(false);
                        setSelectedFollowUp(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}



      </div>
    </>
  );
}

export default Consultant;